import { useCallback, useEffect, useState } from 'react';
import { X, Lock, CreditCard, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../utils/authHeaders';

export default function IyzicoPaymentModal({ isOpen, onClose, orderData, userId }) {
  const [loading, setLoading] = useState(false);
  const [checkoutFormContent, setCheckoutFormContent] = useState(null);

  const initializePayment = useCallback(async () => {
    console.log('='.repeat(80));
    console.log('💳 IYZICO PAYMENT MODAL: Initializing payment');
    console.log('Order data:', orderData);
    console.log('User ID:', userId);
    
    setLoading(true);
    
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/payment/iyzico/checkout`;
      console.log('🌐 API URL:', apiUrl);
      console.log('📤 Request body:', JSON.stringify(orderData, null, 2));
      
      const headers = await getAuthHeaders({
        'Content-Type': 'application/json'
      });
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(orderData)
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Server error:', errorText);
        throw new Error(`Server error: ${response.status}`);
      }

      const result = await response.json();
      console.log('📦 Response data:', result);

      if (result.success) {
        console.log('✅ Checkout form created successfully');
        console.log('Token:', result.token);
        console.log('Payment page URL:', result.payment_page_url);
        console.log('🔄 Redirecting to iyzico payment page...');
        
        // CRITICAL FIX: Redirect to iyzico payment page instead of iframe
        // This allows iyzico to properly redirect back to callback URL after payment
        window.location.href = result.payment_page_url;
      } else {
        console.error('❌ Checkout form creation failed:', result.error);
        toast.error('Ödeme formu oluşturulamadı: ' + result.error);
        onClose();
      }
    } catch (error) {
      console.error('='.repeat(80));
      console.error('❌ PAYMENT INITIALIZATION ERROR');
      console.error('Error:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('='.repeat(80));
      toast.error('Ödeme sistemi hatası');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [onClose, orderData, userId]);

  useEffect(() => {
    if (isOpen && orderData) {
      initializePayment();
    }
  }, [initializePayment, isOpen, orderData]);

  useEffect(() => {
    if (checkoutFormContent) {
      console.log('='.repeat(80));
      console.log('📝 RENDERING IYZICO FORM');
      console.log('Form content length:', checkoutFormContent.length);
      
      const container = document.getElementById('iyzipay-checkout-form');
      if (container) {
        console.log('✅ Container found:', container);
        // Clear previous content
        container.innerHTML = '';
        
        // Extract script content from the HTML
        const scriptMatch = checkoutFormContent.match(/<script[^>]*>([\s\S]*?)<\/script>/i);
        
        if (scriptMatch && scriptMatch[1]) {
          console.log('✅ Script found in form content');
          console.log('Script length:', scriptMatch[1].length);
          
          // Create and execute the script
          const script = document.createElement('script');
          script.type = 'text/javascript';
          script.text = scriptMatch[1];
          container.appendChild(script);
          console.log('✅ Script appended to container');
          
          // Also add a div for iyzico to render into
          const iframeContainer = document.createElement('div');
          iframeContainer.id = 'iyzipay-checkout-form-container';
          container.appendChild(iframeContainer);
          console.log('✅ Iframe container created');
        } else {
          console.log('⚠️ No script found, using innerHTML fallback');
          // Fallback: try to set HTML directly
          container.innerHTML = checkoutFormContent;
        }
        console.log('='.repeat(80));
      } else {
        console.error('❌ Container not found: iyzipay-checkout-form');
      }
    }
  }, [checkoutFormContent]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Lock className="w-6 h-6 text-green-600" />
            <div>
              <h2 className="font-manrope font-bold text-xl">Güvenli Ödeme</h2>
              <p className="text-sm text-gray-600">iyzico ile güvenli ödeme</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-trendyol-orange mx-auto mb-4"></div>
              <p className="text-gray-600">Ödeme formu hazırlanıyor...</p>
            </div>
          ) : (
            <>
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold text-blue-900 mb-2">Güvenlik Bilgileri:</p>
                    <ul className="space-y-1 text-blue-800">
                      <li>✓ 256-bit SSL şifreleme ile korunmaktadır</li>
                      <li>✓ Kart bilgileriniz hiçbir yerde saklanmaz</li>
                      <li>✓ 3D Secure ile doğrulanır</li>
                      <li>✓ PCI DSS Level 1 sertifikalıdır</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">Toplam Tutar:</span>
                  <span className="font-manrope font-bold text-2xl text-trendyol-orange">
                    {orderData?.amount?.toFixed(2)} TL
                  </span>
                </div>
                <p className="text-xs text-gray-500 text-center">
                  Taksit seçenekleri ödeme sayfasında görüntülenecektir
                </p>
              </div>

              <div id="iyzipay-checkout-form" className="min-h-[400px]">
                {!checkoutFormContent && !loading && (
                  <div className="text-center py-12 text-gray-500">
                    Ödeme formu yükleniyor...
                  </div>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
                  <CreditCard className="w-4 h-4" />
                  <span>Visa</span>
                  <span>•</span>
                  <span>Mastercard</span>
                  <span>•</span>
                  <span>American Express</span>
                  <span>•</span>
                  <span>Troy</span>
                </div>
                <p className="text-center text-xs text-gray-400 mt-2">
                  Powered by iyzico
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
